import { Injectable } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import { ContentType } from "../../../app/core/shared/contentTypes";
import { CoreLogService } from "../../../app/core/services/log.service";
import { CoreCOMService } from "../../../app/core/services/com.service";
import { CircularInjectionHelper } from "../../../app/core/services/circularInjectionHelper";
import { IObjectObserver } from "../../../app/core/services/IObjectObserver";
import { ObjectObserverFactory } from "../../../app/core/services/objectObserverFactory";
import { SOTObjectObserverData } from "../../core/services/objectObserver.service";
import { SOTLanguageDataModel, SOTLanguageDMKey } from "../models";
import { LocalKeys, StorageHive } from "../../core/services/valueStorage.service";
import { ISOTLanguageService } from "./ILanguageService";

/** Sprache und Lokalisierung */

/** Verwaltet die Sprachen */
@Injectable()
export class SOTLanguageService implements ISOTLanguageService {

  private _sotLanguageObsServ: IObjectObserver<SOTLanguageDataModel>;
  private _languageChangedSubscription: Subscription;
  private _valueStorageChangedSubscription: Subscription;
  private _languageChanged: Subject<void> = new Subject<void>();

  constructor(private _coreLogService: CoreLogService,
    private _coreCOMService: CoreCOMService,
    private _circularInjectionHelper: CircularInjectionHelper) {
    const me = this;

    me._valueStorageChangedSubscription = me._coreCOMService.environment.valueStorage.ObserveChanges.subscribe(async (hiveAndKeys) => {
      if (hiveAndKeys.some((entry) => entry.hive == StorageHive.Local && entry.key == LocalKeys.sotLocation)) {
        // try rebind
        await me.bindSOTLanguage();
      }
    });
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._languageChangedSubscription) {
      me._languageChangedSubscription.unsubscribe();
    }
    if (me._valueStorageChangedSubscription) {
      me._valueStorageChangedSubscription.unsubscribe();
    }
  }

  /** bindung an die sot languages, if need */
  private async bindSOTLanguage(): Promise<void> {
    const me = this;
    const location = await me._coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.sotLocation);
    if (location) {
      // is new location other then momently used
      if (location != await me.getCurrentLanguageLocation()) {
        const data = new SOTObjectObserverData(location);
        // produkte seperat laden (um selbe bilder nicht doppelt und Producte ohne Bilder nicht zu laden)
        me._sotLanguageObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<SOTLanguageDataModel>(ContentType.SOTLanguages, me._circularInjectionHelper, data);
        if (me._languageChangedSubscription) {
          me._languageChangedSubscription.unsubscribe();
          me._languageChangedSubscription = null;
        }
        me._languageChangedSubscription = me._sotLanguageObsServ.ObserveChanges.subscribe(() => {
          me._languageChanged.next();
        });
        me._languageChanged.next();
      } else {
        // do nothing, the binding is correct
      }
    } else {
      // location unbekannt = keine sprach observer
      if (me._sotLanguageObsServ) {
        me._sotLanguageObsServ.Release();
        me._sotLanguageObsServ = null;
        me._languageChanged.next();
      }
    }
  }

  /** event velcher ausgelöst wird, wenn die languagedaten sich ändern */
  public get ObserveChanges(): Observable<void> {
    return this._languageChanged.asObservable();
  }

  /** get location from current sotLanguage, sonst null */
  private async getCurrentLanguageLocation(): Promise<number> {
    const me = this;
    // wenn sot languages da sind verwenden, sonst null
    if (me._sotLanguageObsServ) {
      const ObserverData = await me._sotLanguageObsServ.ObserverData;
      if (ObserverData) {
        return ObserverData["hyp_cus_nr"];
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  /** gets the languages from sotlanguages, else empty */
  public async getLanguages(): Promise<object> {
    const me = this;
    // versuche initial zu binden (lazyload, wegen cirkulärer abhängigkeit)
    if (me._sotLanguageObsServ == null) {
      await me.bindSOTLanguage();
    }
    // wenn languages da sind verwenden, sonst empty
    if (me._sotLanguageObsServ) {
      const sotLanguage = await me._sotLanguageObsServ.GetObject(SOTLanguageDMKey);
      if (sotLanguage) {
        return sotLanguage.languages;
      } else {
        return {}; // empty
      }
    } else {
      return {}; // empty
    }
  }

}
